import { get, writable } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';

import { SyncService } from '@client/services/sync';

const STORAGE_KEY = 'opensense_streaming_queue';

const createStreamingManager = () => {
  const { subscribe, update } = writable({
    streams: new Map(), // streamId -> streamData
    maxConcurrent: 3
  });

  // Load persisted streams on init
  const loadPersistedStreams = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        update((state) => ({
          ...state,
          streams: new Map(Object.entries(parsed.streams || {}))
        }));
      }
    } catch (error) {
      console.warn('Failed to load persisted streams:', error);
    }
  };

  // Persist streams to localStorage
  const persistStreams = (streams) => {
    try {
      const streamObj = Object.fromEntries(streams.entries());
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          streams: streamObj,
          timestamp: Date.now()
        })
      );
    } catch (error) {
      console.warn('Failed to persist streams:', error);
    }
  };

  // Create a new stream
  const createStream = (config) => {
    const streamId = uuidv4();
    const streamData = {
      id: streamId,
      title: config.title || 'Sync Operation',
      type: config.type || 'sync',
      status: 'pending', // pending, running, completed, failed, cancelled
      progress: {
        current: 0,
        total: config.total || null,
        successes: 0,
        failures: 0
      },
      results: [],
      error: null,
      startedAt: Date.now(),
      completedAt: null,
      config,
      oboeStream: null
    };

    update((state) => {
      const newStreams = new Map(state.streams);
      newStreams.set(streamId, streamData);
      persistStreams(newStreams);
      return { ...state, streams: newStreams };
    });

    return streamId;
  };

  // Start a stream
  const startStream = async (streamId) => {
    const state = get({ subscribe });
    const stream = state.streams.get(streamId);

    if (!stream || stream.status !== 'pending') {
      return;
    }

    // Update status to running
    updateStream(streamId, { status: 'running' });

    const { endpoint, scope, payload, pattern } = stream.config;
    const key = getKey(scope, endpoint);
    const streamPattern = pattern || `{${key}}`;
    const isCSV = endpoint === 'synccsv';

    try {
      // Temporarily suppress any global error handling that might interfere
      const originalOnError = window.onerror;

      const oboeStream = SyncService.streamSync(endpoint, scope, payload, streamPattern)
        .node(streamPattern, (node) => {
          const success = node.status === 'synced';
          updateStream(streamId, (current) => ({
            progress: {
              ...current.progress,
              current: current.progress.current + 1,
              successes: success ? current.progress.successes + 1 : current.progress.successes,
              failures: !success ? current.progress.failures + 1 : current.progress.failures
            },
            results: [...current.results, { name: node[key], success, timestamp: Date.now() }]
          }));
        })
        .done(() => {
          // Restore original error handler
          window.onerror = originalOnError;

          updateStream(streamId, {
            status: 'completed',
            completedAt: Date.now(),
            oboeStream: null
          });
        })
        .fail((error) => {
          // Restore original error handler
          window.onerror = originalOnError;

          console.error('Stream failed:', error);

          // Prevent any external error handling from running
          if (error && typeof error === 'object') {
            error.handled = true;
          }

          // For CSV operations, provide more specific error messages
          let errorMessage = 'Stream failed';
          if (isCSV) {
            if (error?.jsonBody?.message?.includes('CSV')) {
              errorMessage = error.jsonBody.message;
            } else if (error?.message?.includes('CSV')) {
              errorMessage = error.message;
            } else {
              errorMessage = 'CSV sync failed - please check your data format';
            }
          } else {
            errorMessage = error?.jsonBody?.message || error?.message || 'Stream failed';
          }

          updateStream(streamId, {
            status: 'failed',
            error: errorMessage,
            completedAt: Date.now(),
            oboeStream: null
          });
        });

      // Store oboe stream reference for cancellation
      updateStream(streamId, { oboeStream });
    } catch (error) {
      console.error('Failed to start stream:', error);

      // Handle CSV validation errors that might occur before streaming starts
      let errorMessage = 'Failed to start stream';
      if (isCSV && error.message) {
        errorMessage = error.message.includes('CSV') ? error.message : 'CSV validation failed';
      } else {
        errorMessage = error.message || 'Failed to start stream';
      }

      updateStream(streamId, {
        status: 'failed',
        error: errorMessage,
        completedAt: Date.now()
      });
    }
  };

  // Update a stream
  const updateStream = (streamId, updates) => {
    update((state) => {
      const newStreams = new Map(state.streams);
      const current = newStreams.get(streamId);

      if (!current) return state;

      const updatedStream =
        typeof updates === 'function' ? { ...current, ...updates(current) } : { ...current, ...updates };

      newStreams.set(streamId, updatedStream);
      persistStreams(newStreams);

      return { ...state, streams: newStreams };
    });
  };

  // Cancel a stream
  const cancelStream = (streamId) => {
    const state = get({ subscribe });
    const stream = state.streams.get(streamId);

    if (stream?.oboeStream) {
      try {
        stream.oboeStream.abort();
      } catch (error) {
        console.warn('Failed to abort stream:', error);
      }
    }

    updateStream(streamId, {
      status: 'cancelled',
      completedAt: Date.now(),
      oboeStream: null
    });
  };

  // Remove a stream from the queue
  const removeStream = (streamId) => {
    update((state) => {
      const newStreams = new Map(state.streams);

      // Cancel if running
      const stream = newStreams.get(streamId);
      if (stream?.status === 'running') {
        cancelStream(streamId);
      }

      newStreams.delete(streamId);
      persistStreams(newStreams);

      return { ...state, streams: newStreams };
    });
  };

  // Clear completed/failed streams
  const clearCompleted = () => {
    update((state) => {
      const newStreams = new Map();

      for (const [id, stream] of state.streams) {
        if (!['completed', 'failed', 'cancelled'].includes(stream.status)) {
          newStreams.set(id, stream);
        }
      }

      persistStreams(newStreams);
      return { ...state, streams: newStreams };
    });
  };

  // Get key field for different scopes (from sync store)
  const getKey = (scope, endpoint) => {
    // For CSV operations, always use email as the key
    if (endpoint === 'synccsv') {
      return 'email';
    }

    switch (scope) {
      case 'groups':
      case 'organizations':
        return 'name';
      default:
        return 'email';
    }
  };

  // Start a sync operation and add to queue
  const startSyncOperation = async (config) => {
    // Pre-validate CSV data if this is a CSV operation
    if (config.endpoint === 'synccsv') {
      // Check if CSV data exists
      if (!config.payload?.parsed) {
        throw new Error('No CSV data provided for sync operation.');
      }

      // Check for parsing errors
      if (config.payload.parsed.errors?.length) {
        throw new Error(
          `CSV contains validation errors: ${config.payload.parsed.errors.map((e) => e.message).join(', ')}`
        );
      }

      // Check if data exists
      if (!config.payload.parsed.data?.length) {
        throw new Error('CSV contains no data to sync.');
      }
    }

    const streamId = createStream({
      title: config.title || `${config.type || 'Sync'} - ${config.scope || 'Users'}`,
      type: 'sync',
      total: config.total,
      endpoint: config.endpoint,
      scope: config.scope,
      payload: config.payload,
      pattern: config.pattern
    });

    await startStream(streamId);
    return streamId;
  };

  // Initialize by loading persisted streams
  loadPersistedStreams();

  return {
    subscribe,
    createStream,
    startStream,
    updateStream,
    cancelStream,
    removeStream,
    clearCompleted,
    startSyncOperation
  };
};

export const streamingManager = createStreamingManager();
